import { ReactElement } from 'react';
import { Box, Button, TableCell, TableRow } from '@mui/material';
import { Styles } from 'common/types/styles';
import { PDPAttributeHistoryV2Item } from 'common/services/api/content/content-api.types';
import { formatMoney } from 'common/utils/numbers';
import { userService } from 'common/services/user.service';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { TruncateContainer } from 'common/ui/containers/truncate-container';
import { WalmartRetailers } from 'common/constants/entities';

const styles: Styles = {
  cellContainer: { display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', width: 250 },
  cell: { minWidth: '150px', wordBreak: 'break-all' },
  copyBtn: { alignSelf: 'flex-end' },
};

interface Props {
  h: PDPAttributeHistoryV2Item;
}

export function PdpHistoryItem({ h }: Props): ReactElement {
  const isL3 = userService.ensureLevel('L3');

  const getCellValue = (value: unknown) => {
    switch (typeof value) {
      case 'object': {
        return JSON.stringify(value ?? '');
      }
      default:
        return String(value ?? '');
    }
  };

  return (
    <TableRow>
      <TableCell> {h.generatedAt ? format(new Date(h.generatedAt), 'dd/MM/yyyy hh:mm:ss') : '-'}</TableCell>
      <TableCell>{h.model}</TableCell>
      {!isL3 && <TableCell>{h.cost ? formatMoney(h.cost, { minimumFractionDigits: 6 }) : '-'}</TableCell>}
      {userService.ensureRetailers(WalmartRetailers) ? (
        <>
          <TableCell>
            <Box sx={styles.cellContainer}>
              {h.generatedValue && (
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(getCellValue(h.generatedValue));
                    toast.success('Message copied!');
                  }}
                  sx={styles.copyBtn}
                  size="small"
                >
                  Copy
                </Button>
              )}
              <TruncateContainer maxHeight={70}>{getCellValue(h.generatedValue)}</TruncateContainer>
            </Box>
          </TableCell>
          <TableCell>
            <Box sx={styles.cellContainer}>
              {h.revisedValue && (
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(getCellValue(h.revisedValue));
                    toast.success('Message copied!');
                  }}
                  sx={styles.copyBtn}
                  size="small"
                >
                  Copy
                </Button>
              )}
              <TruncateContainer maxHeight={70}>{getCellValue(h.revisedValue)}</TruncateContainer>
            </Box>
          </TableCell>
        </>
      ) : (
        <>
          <TableCell>{h.process}</TableCell>
          <TableCell>
            <Box sx={styles.cellContainer}>
              {h.title && (
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(getCellValue(h.title));
                    toast.success('Message copied!');
                  }}
                  sx={styles.copyBtn}
                  size="small"
                >
                  Copy
                </Button>
              )}
              <TruncateContainer maxHeight={70}>{getCellValue(h.title)}</TruncateContainer>
            </Box>
          </TableCell>
          <TableCell>
            <Box sx={styles.cellContainer}>
              {h.description && (
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(getCellValue(h.description));
                    toast.success('Message copied!');
                  }}
                  sx={styles.copyBtn}
                  size="small"
                >
                  Copy
                </Button>
              )}
              <TruncateContainer maxHeight={70}>{getCellValue(h.description)}</TruncateContainer>
            </Box>
          </TableCell>
          <TableCell>
            <Box sx={styles.cellContainer}>
              {h.features && (
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(getCellValue(h.features));
                    toast.success('Message copied!');
                  }}
                  sx={styles.copyBtn}
                  size="small"
                >
                  Copy
                </Button>
              )}
              <TruncateContainer maxHeight={70}>{getCellValue(h.features)}</TruncateContainer>
            </Box>
          </TableCell>
          <TableCell>
            <Box sx={styles.cellContainer}>
              {h.keywords && (
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(getCellValue(h.keywords));
                    toast.success('Message copied!');
                  }}
                  sx={styles.copyBtn}
                  size="small"
                >
                  Copy
                </Button>
              )}
              <TruncateContainer maxHeight={70}>{getCellValue(h.keywords)}</TruncateContainer>
            </Box>
          </TableCell>
        </>
      )}
      <TableCell>
        <Box sx={styles.cellContainer}>
          {h.rationale && (
            <Button
              onClick={() => {
                navigator.clipboard.writeText(getCellValue(h?.rationale));
                toast.success('Message copied!');
              }}
              sx={styles.copyBtn}
              size="small"
            >
              Copy
            </Button>
          )}
          <TruncateContainer maxHeight={70}>{getCellValue(h?.rationale)}</TruncateContainer>
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={styles.cellContainer}>
          {h.requestPayload && (
            <Button
              onClick={() => {
                navigator.clipboard.writeText(getCellValue(h?.requestPayload));
                toast.success('Message copied!');
              }}
              sx={styles.copyBtn}
              size="small"
            >
              Copy
            </Button>
          )}
          <TruncateContainer maxHeight={70}>{getCellValue(h?.requestPayload)}</TruncateContainer>
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={styles.cellContainer}>
          {h?.response && (
            <Button
              onClick={() => {
                navigator.clipboard.writeText(getCellValue(h?.response));
                toast.success('Message copied!');
              }}
              sx={styles.copyBtn}
              size="small"
            >
              Copy
            </Button>
          )}
          <TruncateContainer maxHeight={70}>{getCellValue(h?.response)}</TruncateContainer>
        </Box>
      </TableCell>
    </TableRow>
  );
}
